/**
 * Тип тематики: спрос по месяцам и референсы Pinterest — то, ради чего тип
 * заводят: «кружевное растёт на 89 % — вот как это снимают».
 *
 * Сбор Pinterest ставится в очередь и идёт в reels-worker 1–3 минуты. Пока у
 * типа есть задача в очереди или в работе, экран переспрашивает сервис раз в
 * 3 с; ответ типа несёт и задачи, и пины, поэтому новые пины приходят тем же
 * запросом. Когда сбор кончился — сбрасываем ещё и кэш тематики: там число
 * референсов и обложка типа.
 *
 * Сервис не ставит второй сбор того же типа, пока первый не кончился (вернёт
 * прежнюю задачу с ПРЕЖНИМИ параметрами), — поэтому форма на это время
 * закрыта, иначе новые параметры молча потерялись бы.
 *
 * Слово чужой подборки смотрят и берут из него референсы в библиотеку, но сбор
 * запускает только владелец подборки — формы сбора у остальных нет.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { MonthBars } from "@/components/trends/charts";
import { References } from "@/components/trends/References";
import { TaskList } from "@/components/trends/TaskList";
import { btnPrimary, Empty, ErrorLine, input, label, Loading, Panel, Segmented } from "@/components/trends/ui";
import { BackLink } from "@/shared/ui";
import { useIdentity } from "@/lib/identity";
import { fittingPath } from "@/lib/fitting";
import { num, when } from "@/lib/media";
import {
  canEdit,
  isTaskActive,
  loadType,
  monthLong,
  peakCount,
  pollWhileActive,
  post,
  retryQuery,
  trendKeys,
  yoyText,
  yoyTone,
  type PinScope,
  type TypeDetail,
} from "@/lib/trends";

export function TrendTypePage() {
  const typeId = Number(useParams().typeId);
  const queryClient = useQueryClient();
  const valid = Number.isFinite(typeId) && typeId > 0;

  const detail = useQuery({
    queryKey: trendKeys.type(typeId),
    queryFn: () => loadType(typeId),
    retry: retryQuery,
    enabled: valid,
    refetchInterval: (q) => pollWhileActive(q.state.data?.tasks),
  });

  // Сбор кончился → число референсов и обложка в тематике устарели.
  const activeCount = detail.data?.tasks.filter(isTaskActive).length ?? 0;
  const topicId = detail.data?.topic_id;
  const wasActive = useRef(0);
  useEffect(() => {
    if (wasActive.current > 0 && activeCount === 0 && topicId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: trendKeys.topic(topicId) });
      void queryClient.invalidateQueries({ queryKey: trendKeys.list });
    }
    wasActive.current = activeCount;
  }, [activeCount, topicId, queryClient]);

  if (!valid)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес типа.")} />
      </div>
    );
  if (detail.isPending) return <Loading />;
  if (detail.isError)
    return (
      <div className="grid gap-3 p-5">
        <BackLink to="/trends">Все тематики</BackLink>
        <ErrorLine error={detail.error} />
      </div>
    );

  return <TypeView t={detail.data} />;
}

function TypeView({ t }: { t: TypeDetail }) {
  const queryClient = useQueryClient();
  const active = t.tasks.filter(isTaskActive);
  const peak = peakCount(t);
  const lastMonth = t.months[t.months.length - 1]?.month ?? null;
  const editable = canEdit(t, useIdentity().data?.id);
  const personal = t.owner_user_id !== 0;

  return (
    <div className="grid gap-4 p-5">
      <BackLink to={`/trends/${t.topic_id}`}>{t.topic_name}</BackLink>

      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-annot="шапка типа">
        <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">{t.name}</h1>
        <span className="rounded-[4px] bg-neutral-bg px-1.5 text-[11.5px] text-fg-muted">{t.grp}</span>
        {t.state === "ignored" ? (
          <span className="rounded-[4px] bg-warn-bg px-1.5 text-[11.5px] text-warn">отброшен</span>
        ) : null}
        <span className="text-[12.5px] text-fg-muted">
          фраза <span className="text-fg">«{t.phrase}»</span>
        </span>
        <span className="text-[12.5px] text-fg-faint">
          Wordstat {t.date_collect ? when(t.date_collect) : "ещё не собирал"}
        </span>
      </header>

      {t.months.length ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-annot="статистика типа">
            <Stat title="12 месяцев" value={num(t.year)} sub={`прошлые 12 — ${num(t.year_prev)}`} />
            <Stat
              title="год к году"
              value={<span className={yoyTone(t.yoy)}>{yoyText(t.yoy)}</span>}
              sub={t.yoy === null ? "нет базы прошлого года" : "последние 12 к предыдущим"}
            />
            <Stat title="последний месяц" value={num(t.last_month)} sub={monthLong(lastMonth)} />
            <Stat plain title="пик" value={monthLong(t.peak_month)} sub={peak !== null ? `${num(peak)} запросов` : ""} />
          </div>
          <Panel title="Спрос по месяцам" annot="график спроса">
            <div className="p-3">
              <MonthBars months={t.months} />
            </div>
          </Panel>
        </>
      ) : (
        <Panel annot="график спроса">
          <Empty>
            Истории Wordstat {personal ? "у слова" : "у типа"} пока нет — она приедет после «Обновить из Wordstat» на
            странице{" "}
            <Link to={`/trends/${t.topic_id}`} className="text-accent-text hover:underline">
              {personal ? "подборки" : "тематики"}
            </Link>
            .
          </Empty>
        </Panel>
      )}

      <Panel
        title="Собрать референсы с Pinterest"
        annot="сбор pinterest"
        aside={
          active.length ? (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-warn">
              <Loader2 size={12} className="animate-spin" />
              сбор идёт — сетка обновится сама
            </span>
          ) : null
        }
      >
        {editable ? (
          <PinterestForm
            t={t}
            locked={active.some((x) => x.kind === "pinterest")}
            onQueued={() => queryClient.invalidateQueries({ queryKey: trendKeys.type(t.id) })}
          />
        ) : (
          <div className="px-3 py-2.5 text-[12.5px] text-fg-muted">
            Владелец подборки — {t.owner_name ?? "коллега"}: сбор по её словам запускает только владелец.
          </div>
        )}
        {t.tasks.length ? (
          <div className="border-t border-border">
            <TaskList tasks={t.tasks} limit={active.length ? Math.max(3, active.length) : 3} />
          </div>
        ) : null}
      </Panel>

      {t.searches.length ? (
        <Panel title="История поисков" annot="история поисков">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className="border-b border-border px-3 py-2 text-left">выдача</th>
                  <th className="w-full border-b border-border px-3 py-2 text-left">запрос</th>
                  <th className="border-b border-border px-3 py-2 text-right">страниц</th>
                  <th className="border-b border-border px-3 py-2 text-right" title="Пинов в выдаче">
                    увидено
                  </th>
                  <th className="border-b border-border px-3 py-2 text-right" title="Лучших (по сохранениям, по скорости и свежих) скачано целиком">
                    скачано
                  </th>
                  <th className="border-b border-border px-3 py-2 text-left">кто</th>
                  <th className="border-b border-border px-3 py-2 text-right">когда</th>
                </tr>
              </thead>
              <tbody>
                {t.searches.map((s) => (
                  <tr key={s.id} className="hover:bg-bg-hover">
                    <td className="border-b border-border px-3 py-1.5 whitespace-nowrap">
                      {s.scope === "videos" ? "видео" : "картинки"}
                    </td>
                    <td className="border-b border-border px-3 py-1.5">{s.text}</td>
                    <td className="border-b border-border px-3 py-1.5 text-right tnum">{num(s.pages)}</td>
                    <td className="border-b border-border px-3 py-1.5 text-right tnum text-fg-muted">{num(s.seen)}</td>
                    <td className="border-b border-border px-3 py-1.5 text-right tnum">{num(s.downloaded)}</td>
                    <td className="border-b border-border px-3 py-1.5 whitespace-nowrap text-fg-muted">
                      {s.author ?? "—"}
                    </td>
                    <td className="border-b border-border px-3 py-1.5 text-right whitespace-nowrap tnum text-fg-muted">
                      {when(s.date_create)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      <References
        pins={t.pins}
        scope="type"
        queryKey={trendKeys.type(t.id)}
        defaultLibraryName={() => `${t.topic_name} · ${t.name}`}
        emptyText="Референсов пока нет — соберите их с Pinterest формой выше."
        fittingPath={(ids) => fittingPath({ pins: ids })}
      />
    </div>
  );
}

function Stat({ title, value, sub, plain = false }: { title: string; value: ReactNode; sub: string; plain?: boolean }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2.5">
      <div className="text-[11.5px] text-fg-faint">{title}</div>
      <div className={`mt-0.5 text-[20px] font-semibold leading-tight tracking-[-0.01em] text-fg ${plain ? "" : "tnum"}`}>
        {value}
      </div>
      <div className="mt-0.5 min-h-[18px] text-[11.5px] text-fg-muted">{sub}</div>
    </div>
  );
}

function PinterestForm({ t, locked, onQueued }: { t: TypeDetail; locked: boolean; onQueued: () => void }) {
  const [scope, setScope] = useState<PinScope>("pins");
  const [pages, setPages] = useState(15);
  const [top, setTop] = useState(40);
  const [text, setText] = useState("");
  const [queued, setQueued] = useState(false);

  const start = useMutation({
    mutationFn: () =>
      post<{ task_id: number }>(`/api/trend-types/${t.id}/pinterest`, {
        text: text.trim(),
        scope,
        pages: clamp(pages, 1, 40, 15),
        top: clamp(top, 1, 200, 40),
      }),
    onSuccess: () => {
      setQueued(true);
      onQueued();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!locked && !start.isPending) start.mutate();
  };

  return (
    <form onSubmit={submit} className="grid gap-2 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <Segmented<PinScope>
          label="выдача"
          value={scope}
          onPick={setScope}
          options={[
            { value: "pins", label: "Картинки", title: "Обычная выдача пинов" },
            { value: "videos", label: "Видео", title: "Выдача видео-пинов; mp4 скачивается вместе с обложкой" },
          ]}
        />
        <label className="grid gap-1">
          <span className={label} title="Страница выдачи — около 24 пинов">
            страниц (1–40)
          </span>
          <input
            type="number"
            min={1}
            max={40}
            className={`${input} w-[90px] tnum`}
            value={pages}
            onChange={(e) => setPages(Number(e.target.value))}
          />
        </label>
        <label className="grid gap-1">
          <span
            className={label}
            title="Топ столько-то по сохранениям, столько же по скорости и столько же самых свежих: у них снимаем карточку (репины, пересылки) и качаем оригинал и ролик. Превью качается всем."
          >
            лучших (1–200)
          </span>
          <input
            type="number"
            min={1}
            max={200}
            className={`${input} w-[110px] tnum`}
            value={top}
            onChange={(e) => setTop(Number(e.target.value))}
          />
        </label>
        <label className="grid min-w-[220px] flex-1 gap-1">
          <span className={label}>свой запрос (необязательно)</span>
          <input
            className={`${input} w-full`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t.phrase}
            maxLength={255}
          />
        </label>
        <button type="submit" className={btnPrimary} disabled={locked || start.isPending}>
          <Search size={13} />
          {start.isPending ? "Ставлю…" : "Собрать"}
        </button>
      </div>
      <p className="text-[11.5px] text-fg-faint">
        {locked
          ? "Сбор этого типа уже идёт — новый можно поставить, когда он закончится."
          : queued
            ? "Сбор поставлен в очередь: 3–8 минут, сетка ниже обновится сама."
            : "Сбор идёт в фоне 3–8 минут: листает выдачу, превью — всем пинам, у лучших (по сохранениям, скорости и свежести) — карточка с репинами, оригинал и ролик."}
      </p>
      <ErrorLine error={start.error} />
    </form>
  );
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}
